import { useEffect, type ReactNode } from "react";
import { motion } from "motion/react";

export function LegalLinks({ accountNotice = false }: { accountNotice?: boolean }) {
  return (
    <nav className={`legal-links ${accountNotice ? "account-legal-links" : ""}`} aria-label="Legal information">
      <a href="/about">About</a>
      <span aria-hidden="true">·</span>
      <a href="/privacy">Privacy Policy</a>
      <span aria-hidden="true">·</span>
      <a href="/terms">Terms of Service</a>
    </nav>
  );
}

function LegalPage({ title, summary, children }: { title: string; summary: string; children: ReactNode }) {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = `${title} | CRAFTOFF`;
    return () => { document.title = previousTitle; };
  }, [title]);

  return (
    <motion.section className="legal-screen" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <header className="legal-header">
        <a className="exit-button legal-home-link" href="/">← HOME</a>
        <div><h1>{title}</h1><p>{summary}</p></div>
      </header>
      <article className="legal-document">
        {children}
        <footer><LegalLinks /></footer>
      </article>
    </motion.section>
  );
}

export function PrivacyPolicyScreen() {
  return (
    <LegalPage title="Privacy Policy" summary="How Craftoff handles player information">
      <p className="legal-updated">Last updated: <time dateTime="2026-07-21">July 21, 2026</time></p>

      <section>
        <h2>Overview</h2>
        <p>Craftoff is a competitive crafting game. This policy explains what information we collect, why we use it, and the choices available to you. We do not sell your personal information.</p>
      </section>

      <section>
        <h2>Information we collect</h2>
        <ul>
          <li><strong>Guest play:</strong> a random guest identifier stored in a cookie, XP, match outcomes, and other game progress needed to continue your session.</li>
          <li><strong>Account information:</strong> your chosen public username, email address, and a securely hashed version of your password. Craftoff does not store your readable password.</li>
          <li><strong>Game and safety data:</strong> leaderboard results, reports, security events, request logs, device or browser information, and approximate network information such as an IP address.</li>
        </ul>
      </section>

      <section>
        <h2>How we use information</h2>
        <p>We use this information to operate Craftoff, authenticate accounts, maintain account progression and ratings, show public usernames on leaderboards, prevent abuse, investigate reports, troubleshoot problems, and improve reliability.</p>
      </section>

      <section>
        <h2>Cookies and local storage</h2>
        <p>Craftoff uses an essential session cookie and browser storage to keep you signed in and retain game preferences. These technologies are required for the features you request and are not used to sell or share advertising profiles.</p>
      </section>

      <section>
        <h2>Service providers</h2>
        <p>Craftoff uses Cloudflare to host the game, store game data, provide security checks, and process network requests. We disclose information only when needed to provide these services, protect players, comply with law, or complete a business transfer with appropriate safeguards.</p>
      </section>

      <section>
        <h2>Retention and deletion</h2>
        <p>We retain account and operational records while needed to provide the service, maintain fair rankings, resolve disputes, prevent abuse, and meet legal obligations. Guest sessions do not earn transferable progression. You may request access to or deletion of your personal information by emailing <a href="mailto:privacy@craftoff.io">privacy@craftoff.io</a>.</p>
      </section>

      <section>
        <h2>Children</h2>
        <p>Craftoff is not directed to children under 13. If you believe a child provided personal information without appropriate consent, contact us so we can review and remove it.</p>
      </section>

      <section>
        <h2>Security and changes</h2>
        <p>We use reasonable technical and organizational safeguards, but no online service can guarantee absolute security. We may update this policy as Craftoff changes. The date above will identify the latest version.</p>
      </section>

      <section>
        <h2>Contact</h2>
        <p>For privacy questions or account-data requests, email <a href="mailto:privacy@craftoff.io">privacy@craftoff.io</a>.</p>
      </section>
    </LegalPage>
  );
}

export function TermsOfServiceScreen() {
  return (
    <LegalPage title="Terms of Service" summary="The rules for playing Craftoff">
      <p className="legal-updated">Last updated: <time dateTime="2026-07-23">July 23, 2026</time></p>

      <section>
        <h2>Agreement</h2>
        <p>By accessing or playing Craftoff, you agree to these terms and the <a href="/privacy">Privacy Policy</a>. If you do not agree, do not use the service.</p>
      </section>

      <section>
        <h2>Eligibility and accounts</h2>
        <p>You must be at least 13 years old, or the minimum age required in your country, to create an account. You are responsible for activity performed through your account and for keeping your password secure. Guest play is tied to the browser session and does not earn transferable XP, rating, wins, or losses.</p>
      </section>

      <section>
        <h2>Fair play</h2>
        <p>You may not cheat, automate gameplay, exploit bugs, interfere with matches, harass other players, impersonate others, manipulate ratings, submit false reports, probe the service for vulnerabilities, or use Craftoff in a way that harms the game or its players. We may restrict or remove access, ratings, usernames, or content when reasonably necessary to protect the service.</p>
      </section>

      <section>
        <h2>XP, rankings, coins, and shop items</h2>
        <p>XP, ratings, rankings, coins, cosmetics, consumable battle items, and other virtual items have no cash value and do not represent ownership or a financial account. Coins are non-transferable, cannot be redeemed for money, and may only be spent on eligible in-game shop items. We may rebalance, correct, reset, or remove virtual items to address errors, cheating, game changes, or service closure.</p>
      </section>

      <section>
        <h2>Your content</h2>
        <p>You retain rights in content you submit, such as a username or report. You give Craftoff permission to store, display, and process that content only as needed to operate, secure, and improve the service. Do not submit unlawful, infringing, abusive, or deceptive content.</p>
      </section>

      <section>
        <h2>Ownership and game disclaimer</h2>
        <p>Craftoff’s original software, interface, branding, and game systems are protected by applicable intellectual-property laws. Craftoff is an independent project and is not affiliated with, endorsed by, or sponsored by Mojang Studios or Microsoft. Minecraft names, imagery, and related rights belong to their respective owners.</p>
      </section>

      <section>
        <h2>Availability and disclaimers</h2>
        <p>Craftoff is provided on an “as is” and “as available” basis. Features may change, experience interruptions, or be discontinued. To the extent permitted by law, we disclaim implied warranties and are not liable for indirect, incidental, special, consequential, or punitive damages arising from your use of the service.</p>
      </section>

      <section>
        <h2>Changes and termination</h2>
        <p>We may update these terms as the game evolves. Continued use after an updated version is posted means you accept the revised terms. You may stop using Craftoff at any time. We may suspend access when these terms are violated or when necessary for security, legal compliance, or service integrity.</p>
      </section>

      <section>
        <h2>Contact</h2>
        <p>Questions about these terms may be sent to <a href="mailto:legal@craftoff.io">legal@craftoff.io</a>.</p>
      </section>
    </LegalPage>
  );
}
